import Card from '@mui/material/Card'
import {MouseEvent, useContext, useEffect, useRef, useState} from 'react'
import styled from 'styled-components'
import {HistoryContext} from '../../../../../contexts/history'
import Tooltip, {StyledTooltip} from '../../../../../globalComponents/tooltip'
import {useLoading} from '../../../../../globalComponents/useLoading'
import {
    AssistantApi,
    ExportFormat,
} from '../../../../../services/AssistantApi.service'
import {messageType} from '../../../../../types/message'
import {getCurrentColors} from '../../../../../utils/colors'
import {copyToClipboard} from '../../../../../utils/copyToClippboard'
import Modal from '@mui/material/Modal'
import Box from '@mui/material/Box'
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined'
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import CircularProgress from '@mui/material/CircularProgress'
import {toast} from 'react-toastify'

const COLORS = getCurrentColors() // Importando as cores do tema atual

const Background = styled.div`
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    position: fixed;
    background-color: rgba(0, 0, 0, 0.5);
`

const FeedbackButton = styled.button`
    padding: 5px 10px;
    background-color: ${COLORS.cinzaDesbotado};
    border: 2px solid ${COLORS.cinzaDesbotado};
    border-radius: 5px;
    display: inline-block;
    width: auto;
    min-width: 100px;
    height: 30px;
    cursor: pointer;
    z-index: 99999999;
    align-self: flex-end;
    color: ${COLORS.cinzaEscuro};
`
const FeedbackWrapper = styled.div`
    position: absolute;
    top: calc(100% - 10px);
    left: 0;
    padding: 8px;
    z-index: 99999999;
    display: flex;
    flex-direction: column;
    gap: 6px;
    border: 2px solid ${COLORS.cinzaDesbotado};
    border-radius: 12px;
    width: 600px;
    margin: 0 auto;
`

const ToolsButton = styled.button`
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    background-color: transparent;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font: inherit;
    font-size: 14px;
    color: #757575;
    &:hover:not(:disabled),
    &[aria-expanded='true'] {
        background-color: ${COLORS.secundariaEscura}26;
    }
    &:disabled {
        cursor: default;
        opacity: 0.6;
    }
    &:hover ${StyledTooltip} {
        visibility: visible;
    }
`
const FeedbackTextArea = styled.textarea`
    padding: 5px;
    outline: 0;
    border-radius: 6px;
    z-index: 99999999;
    position: relative;
    border: 2px solid ${COLORS.cinzaDesbotado};
    width: 100%;
    min-height: 50px;
    resize: none;
    overflow-y: hidden;
    font-size: 14px;
    line-height: 1.4;
`

const StyledTools = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 12px 0 8px;
    padding-top: 8px;
    border-top: 1px solid ${COLORS.secundariaEscura}65;
    position: relative;
`

const TimestampWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    padding-left: 12px;
`

const Timestamp = styled.span`
    font-size: 12px;
    color: #9E9E9E;
`

const ToastMessage = styled.span`
    font-size: 12px;
    color: ${COLORS.cinzaEscuro};
    position: absolute;
    top: 12px;
    left: 90px;
    cursor: auto;
`

const StyledModal = styled(Modal)`
    display: flex;
    align-items: center;
    justify-content: center;
`

const ModalContent = styled(Box)`
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    width: 400px;
    max-width: 90%;
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const ModalLinkButton = styled.button<{$color: string}>`
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
    color: ${(p) => p.$color};
`

const ModalTextArea = styled.textarea`
    width: 100%;
    min-height: 100px;
    padding: 10px;
    font-size: 16px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    background-color: ${COLORS.cinzaDesbotado};
    border-bottom: 1px solid ${COLORS.cinzaEscuro};
    resize: none;
`

const CloseButton = styled.button`
    padding: 8px 12px;
    background-color: ${COLORS.cinzaDesbotado};
    border: none;
    border-radius: 5px;
    cursor: pointer;
    align-self: flex-end;
    font-size: 14px;
`

const EXPORT_ERROR = 'Não foi possível gerar o arquivo. Tente novamente.'

const MenuHeader = styled.div`
    padding: 8px 16px 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #9E9E9E;
`

const ExportMenuItem = styled(MenuItem)`
    &&:hover,
    &&.Mui-focusVisible {
        background-color: ${COLORS.secundariaEscura}26;
    }
`

const FormatExt = styled.span`
    margin-left: auto;
    padding-left: 24px;
    font-size: 13px;
    color: #9E9E9E;
`

interface ToolsProps {
    message: messageType
    lastAssistantMessage: boolean
}

export default function Tools({lastAssistantMessage, message}: ToolsProps) {
    const {selected, pendingChatExports, registerChatExport} =
        useContext(HistoryContext)
    const {Spinner, setIsLoading, isLoading} = useLoading()
    const toastRef = useRef<HTMLSpanElement>(null)

    const [feedbackMessage, setFeedbackMessage] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [feedbackGiven, setFeedbackGiven] = useState<
        'like' | 'dislike' | null
    >(null)

    const [showInput, setShowInput] = useState(false)
    const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null)
    const [isRequestingExport, setIsRequestingExport] = useState(false)
    const [exportId, setExportId] = useState<string | null>(null)
    const isExporting =
        isRequestingExport ||
        (!!exportId && pendingChatExports.includes(exportId))
    const mountedRef = useRef(true)
    const exportingRef = useRef(false)
    const canExport = !!message.guid && !!message.thread_id

    useEffect(() => {
        if (!canExport) setExportAnchor(null)
    }, [canExport])

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    const messageTime = (() => {
        const createdAtRaw = String(message.created_at)
        const createdAtValue = createdAtRaw.match(/(z|[+-]\d{2}:?\d{2})$/i)
            ? createdAtRaw
            : `${createdAtRaw}Z`
        const createdAt = new Date(createdAtValue)
        if (Number.isNaN(createdAt.getTime())) {
            return ''
        }

        return createdAt.toLocaleTimeString('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        })
    })()

    async function handleCopy() {
        if (toastRef.current) return

        const cleanContent = (message.response || message.content)
            .replace(/\*\*\*(.*?)\*\*\*/g, '$1')
            .replace(/\*\*(.*?)\*\*/g, '$1')
            .replace(/\*(.*?)\*/g, '$1')
            .replace(/^[\*\-]\s+/gm, '')

        const answer = await copyToClipboard(cleanContent)
        toastMessage(answer)
    }

    async function handleRegenerate() {
        try {
            setIsLoading(true)
            const api = new AssistantApi()
            await api.regenerateAnswer(message.thread_id)
        } catch (error: any) {
            console.error('Error regenerating answer:', error)
            toastMessage('Erro ao gerar nova resposta. Tente novamente.')
        } finally {
            setIsLoading(false)
        }
    }

    async function handleExport(exportFormat: ExportFormat) {
        setExportAnchor(null)
        if (exportingRef.current || !message.guid || !message.thread_id) return
        exportingRef.current = true
        setIsRequestingExport(true)
        try {
            const api = new AssistantApi()
            const {guid: export_id} = await api.exportMessage(
                message.thread_id,
                message.guid,
                exportFormat
            )
            // Download/erro/timeout tratados no HistoryContext via WebSocket.
            registerChatExport(export_id)
            if (mountedRef.current) setExportId(export_id)
        } catch (error: any) {
            if (mountedRef.current) toast.error(EXPORT_ERROR)
        } finally {
            exportingRef.current = false
            if (mountedRef.current) setIsRequestingExport(false)
        }
    }

    async function handleReport(type: 'like' | 'dislike') {
        if (!message.guid) return
        if (!selected || !feedbackMessage.trim()) {
            toastMessage('A mensagem de feedback não pode estar vazia.')
            return
        }

        try {
            setIsSubmitting(true)
            setIsLoading(true)
            const api = new AssistantApi()
            const response = await api.reportAnswer(
                feedbackMessage,
                message.guid
            )
            toastMessage('Obrigado pelo feedback.')
            setFeedbackMessage('')
            setFeedbackGiven(type)
        } catch (error: any) {
            toastMessage('Erro ao registrar feedback. Tente novamente.')
        } finally {
            setIsLoading(false)
            setIsSubmitting(false)
            setShowInput(false)
        }
    }

    function toastMessage(message: string) {
        if (!toastRef.current) return
        toastRef.current.innerHTML = message
        setTimeout(() => {
            if (!toastRef.current) return
            toastRef.current.innerHTML = ''
        }, 2000)
    }

    function handleTextAreaChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
        const target = e.target
        target.style.height = 'auto'
        target.style.height = `${target.scrollHeight}px`
        setFeedbackMessage(target.value)
        scrollDown()
    }

    function scrollDown() {
        if (lastAssistantMessage)
            document
                .querySelector('#feedback-anchor')
                ?.scrollIntoView({block: 'end'})
    }

    useEffect(() => {
        if (showInput == true) {
            scrollDown()
            console.log(showInput)
        }
    }, [showInput])

    return (
        <>
            <StyledTools>
                <ToolsButton
                    aria-label="Copiar"
                    disabled={isLoading}
                    onClick={handleCopy}
                >
                    <Tooltip left="0" top="calc(100% + 4px)" text="Copiar" />
                    <ContentCopyOutlinedIcon
                        fontSize="small"
                        sx={{color: '#9E9E9E'}}
                    />
                </ToolsButton>

                <ToolsButton
                    aria-label="Gostei"
                    disabled={feedbackGiven !== null}
                    onClick={() => {
                        setFeedbackMessage('Gostei')
                        setTimeout(() => {
                            handleReport('like')
                        }, 0)
                    }}
                >
                    <Tooltip left="0" top="calc(100% + 4px)" text="Gostei" />
                    <ThumbUpAltOutlinedIcon
                        fontSize="small"
                        sx={{
                            color:
                                feedbackGiven === 'like'
                                    ? COLORS.primaria
                                    : '#9E9E9E',
                        }}
                    />
                </ToolsButton>
                <ToolsButton
                    aria-label="Não gostei"
                    disabled={feedbackGiven !== null}
                    onClick={() => setShowInput(true)}
                >
                    <Tooltip
                        left="0"
                        top="calc(100% + 4px)"
                        text="Não gostei"
                    />
                    <ThumbDownAltOutlinedIcon
                        fontSize="small"
                        sx={{
                            color:
                                feedbackGiven === 'dislike'
                                    ? COLORS.primaria
                                    : '#9E9E9E',
                        }}
                    />
                </ToolsButton>
                <Spinner />
                {lastAssistantMessage && (
                    <ToolsButton
                        aria-label="Nova resposta"
                        disabled={isLoading}
                        onClick={handleRegenerate}
                    >
                        <AutorenewOutlinedIcon
                            fontSize="small"
                            sx={{color: '#9E9E9E'}}
                        />
                        <Tooltip
                            left="0"
                            top="calc(100% + 4px)"
                            text="Nova resposta"
                        />
                    </ToolsButton>
                )}
                {canExport && (
                    <ToolsButton
                        disabled={isLoading || isExporting}
                        onClick={(e: MouseEvent<HTMLButtonElement>) =>
                            setExportAnchor(e.currentTarget)
                        }
                        aria-label={
                            isExporting
                                ? 'Baixando resposta'
                                : 'Baixar resposta'
                        }
                        aria-haspopup="menu"
                        aria-expanded={exportAnchor ? 'true' : undefined}
                        aria-busy={isExporting}
                    >
                        {isExporting ? (
                            <CircularProgress
                                size={18}
                                sx={{color: '#9E9E9E'}}
                            />
                        ) : (
                            <FileDownloadOutlinedIcon
                                fontSize="small"
                                sx={{color: '#9E9E9E'}}
                            />
                        )}
                        {!exportAnchor && (
                            <Tooltip
                                left="0"
                                top="calc(100% + 4px)"
                                text="Baixar resposta"
                            />
                        )}
                    </ToolsButton>
                )}
                {messageTime && (
                    <TimestampWrapper>
                        <AccessTimeIcon
                            fontSize="inherit"
                            sx={{color: '#9E9E9E'}}
                        />
                        <Timestamp>{messageTime}</Timestamp>
                    </TimestampWrapper>
                )}
            </StyledTools>

            <Menu
                anchorEl={exportAnchor}
                open={canExport && Boolean(exportAnchor)}
                onClose={() => setExportAnchor(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}
                slotProps={{
                    paper: {
                        sx: {
                            width: 260,
                            borderRadius: '12px',
                            mt: 0.5,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
                        },
                    },
                }}
            >
                <MenuHeader>Baixar resposta</MenuHeader>
                <ExportMenuItem
                    aria-label="Word"
                    onClick={() => handleExport('docx')}
                >
                    <DescriptionOutlinedIcon
                        fontSize="small"
                        sx={{color: '#1976D2', mr: 1.5}}
                    />
                    Word
                    <FormatExt>.docx</FormatExt>
                </ExportMenuItem>
                <ExportMenuItem
                    aria-label="PDF"
                    onClick={() => handleExport('pdf')}
                >
                    <PictureAsPdfOutlinedIcon
                        fontSize="small"
                        sx={{color: '#D34E4E', mr: 1.5}}
                    />
                    PDF
                    <FormatExt>.pdf</FormatExt>
                </ExportMenuItem>
            </Menu>

            <StyledModal open={showInput} onClose={() => setShowInput(false)}>
                <ModalContent>
                    <h3>Dar feedback</h3>
                    <h4 style={{color: '#707070'}}>Explique melhor: </h4>
                    <ModalTextArea
                        autoFocus
                        value={feedbackMessage}
                        placeholder="O que deseja reportar?"
                        onChange={handleTextAreaChange}
                    />

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px',
                        }}
                    >
                        <span
                            style={{color: '#D34E4E', cursor: 'pointer'}}
                            onClick={() => setShowInput(false)}
                        >
                            Cancelar
                        </span>
                        <span
                            style={{color: '#1976D2', cursor: 'pointer'}}
                            onClick={() => handleReport('dislike')}
                        >
                            {isSubmitting ? 'Enviando...' : 'Enviar'}
                        </span>
                    </div>
                </ModalContent>
            </StyledModal>
        </>
    )
}
